import {
	CircleAlertIcon,
	FunctionSquareIcon,
	LoaderCircleIcon,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChildThreadInline } from "./ChildThreadInline";
import { toolResultError, unwrapToolResult } from "./messageHelpers";
import type { ThreadChild } from "./types";

function formatJson(value: unknown): string {
	if (value == null) return "";
	if (typeof value === "string") return value;
	return JSON.stringify(value, null, 2);
}

function extractChildThreadId(output: unknown): string | undefined {
	if (!output || typeof output !== "object") return undefined;
	const obj = output as Record<string, unknown>;
	if (typeof obj.childClericalThreadId === "string") {
		return obj.childClericalThreadId;
	}
	// tool-result envelope: { type, value }
	if (obj.value && typeof obj.value === "object") {
		const val = obj.value as Record<string, unknown>;
		if (typeof val.childClericalThreadId === "string") {
			return val.childClericalThreadId;
		}
	}
	return undefined;
}

export function ToolCallEntry({
	toolName,
	input,
	result,
	pending,
	childThreads,
}: {
	toolName: string;
	input: unknown;
	result?: unknown;
	pending?: boolean;
	childThreads: Map<string, ThreadChild>;
}) {
	const childThreadId = result ? extractChildThreadId(result) : undefined;
	const childThread = childThreadId
		? childThreads.get(childThreadId)
		: undefined;
	const errorText = toolResultError(result);
	const output = unwrapToolResult(result);
	return (
		<div className="mt-2 space-y-2">
			<Card size="sm" className="border border-dashed">
				<CardContent>
					<div className="flex items-center gap-2">
						{errorText ? (
							<CircleAlertIcon className="size-4 text-destructive" />
						) : (
							<FunctionSquareIcon className="size-4 text-muted-foreground" />
						)}
						<span className="font-mono text-sm font-medium">{toolName}</span>
						{pending && (
							<LoaderCircleIcon className="size-3.5 animate-spin text-muted-foreground" />
						)}
						{!pending && result !== undefined && (
							<Badge
								variant={errorText ? "destructive" : "secondary"}
								className="text-xs"
							>
								{errorText ? "failed" : "completed"}
							</Badge>
						)}
					</div>
					<Collapsible className="mt-2">
						<CollapsibleTrigger className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
							Input
						</CollapsibleTrigger>
						<CollapsibleContent>
							<ScrollArea
								orientation="horizontal"
								className="mt-1 rounded-md bg-muted"
							>
								<pre className="p-2 text-xs">{formatJson(input)}</pre>
							</ScrollArea>
						</CollapsibleContent>
					</Collapsible>
					{result !== undefined && !childThread && (
						<Collapsible className="mt-2">
							<CollapsibleTrigger className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
								Output
							</CollapsibleTrigger>
							<CollapsibleContent>
								<ScrollArea
									orientation="horizontal"
									className="mt-1 rounded-md bg-muted"
								>
									<pre className="p-2 text-xs">{formatJson(output)}</pre>
								</ScrollArea>
							</CollapsibleContent>
						</Collapsible>
					)}
				</CardContent>
			</Card>
			{childThread && (
				<ChildThreadInline childThread={childThread} result={result} />
			)}
		</div>
	);
}
